import { useEffect, useMemo, useState } from 'react';

export const DEFAULT_PAGE_SIZE = 50;

/**
 * Client-side pagination for in-memory lists (tables, logs, etc.).
 * Resets to page 1 when resetKey or item count changes materially.
 */
export function useClientPagination(items, resetKey = '', pageSize = DEFAULT_PAGE_SIZE) {
  const [currentPage, setCurrentPage] = useState(1);
  const list = items || [];
  const totalCount = list.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize) || 1);

  useEffect(() => {
    setCurrentPage(1);
  }, [resetKey]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const safePage = Math.min(Math.max(1, currentPage), totalPages);

  const paginatedItems = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return list.slice(start, start + pageSize);
  }, [list, safePage, pageSize]);

  const showingFrom = totalCount === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const showingTo = Math.min(safePage * pageSize, totalCount);

  return {
    items: paginatedItems,
    currentPage: safePage,
    totalPages,
    totalCount,
    pageSize,
    setCurrentPage,
    showingFrom,
    showingTo,
  };
}
